import clsx from "clsx";
import React from "react";

export type StatsCardProps = {
  icon?: React.ReactNode;
  /**
   * Convenience for passing an image URL/path instead of a React node icon.
   * `icon` takes precedence over `iconSrc` when both are provided.
   */
  iconSrc?: string;
  iconAlt?: string;
  iconImgClassName?: string;
  value: React.ReactNode;
  /**
   * Optional suffix rendered on the same row as `value` (e.g. "1" + "Order").
   */
  valueSuffix?: React.ReactNode;
  valueSuffixClassName?: string;
  label: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  containerClassName?: string;
  iconClassName?: string;
  valueClassName?: string;
  labelClassName?: string;
  selectedContainerClassName?: string;
  unselectedContainerClassName?: string;
  selectedValueClassName?: string;
  unselectedValueClassName?: string;
  selectedLabelClassName?: string;
  unselectedLabelClassName?: string;
  ariaLabel?: string;
};

export default function StatsCard(props: StatsCardProps) {
  const {
    icon,
    iconSrc,
    iconAlt,
    iconImgClassName,
    value,
    valueSuffix,
    valueSuffixClassName,
    label,
    selected = false,
    onClick,
    disabled = false,
    containerClassName,
    iconClassName,
    valueClassName,
    labelClassName,
    selectedContainerClassName,
    unselectedContainerClassName,
    selectedValueClassName,
    unselectedValueClassName,
    selectedLabelClassName,
    unselectedLabelClassName,
    ariaLabel,
  } = props;

  const Component = onClick ? "button" : "div";

  const resolvedIcon =
    icon ??
    (iconSrc ? (
      <img src={iconSrc} alt={iconAlt ?? ""} className={clsx("h-4 w-4", iconImgClassName)} />
    ) : null);

  return (
    <Component
      {...(onClick
        ? { type: "button" as const, onClick: disabled ? undefined : onClick, disabled }
        : { onClick: undefined })}
      aria-label={ariaLabel}
      className={clsx(
        "flex h-[84px]  flex-col justify-center items-start gap-[4px] flex-1 rounded-[10px] border text-left overflow-hidden",
        onClick && "cursor-pointer transition-all duration-200",
        disabled && "opacity-60 cursor-not-allowed",
        selected
          ? selectedContainerClassName ?? "border-[#0068FF]"
          : unselectedContainerClassName ?? "border-[#E6E7EB]",
        containerClassName,
      )}
    >
      <div
        className={clsx(
          "flex items-center gap-[3px] whitespace-nowrap font-semibold leading-none min-w-0 ",
          selected ? selectedValueClassName ?? "text-[#0068FF]" : unselectedValueClassName ?? "text-black",
          valueClassName,
        )}
      >
        {resolvedIcon ? <span className={clsx("shrink-0", iconClassName)}>{resolvedIcon}</span> : null}
        <span className="flex items-baseline gap-1 min-w-0 ">
          <span className="shrink-0">{value}</span>
          {valueSuffix !== undefined && valueSuffix !== null ? (
            <span className={clsx("", valueSuffixClassName)}>{valueSuffix}</span>
          ) : null}
        </span>
      </div>
      <div
        className={clsx(
          "whitespace-nowrap font-semibold leading-none w-full",
          selected ? selectedLabelClassName ?? "text-[#0068FF]" : unselectedLabelClassName ?? "text-[#687183]",
          labelClassName,
        )}
      >
        {label}
      </div>
    </Component>
  );
}
